import { useCallback, useMemo } from "react";
import type { HierarchicalItem } from "../../../types/uiTypes";

export function useTreeData<T extends HierarchicalItem>(data: T[] | undefined) {
  // Build tree structure from flat list
  const buildTree = useCallback((itemList: T[]): T[] => {
    const map = new Map<string, T>();
    const roots: T[] = [];

    // First pass: create map
    itemList.forEach(item => {
      map.set(item.id, { ...item, children: [] } as T);
    });

    // Second pass: build tree
    itemList.forEach(item => {
      const node = map.get(item.id)!;
      if (item.parentId && map.has(item.parentId)) {
        const parent = map.get(item.parentId)!;
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Sort children by display order
    const sortChildren = (nodes: T[]) => {
      nodes.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      nodes.forEach(node => {
        if (node.children) sortChildren(node.children as T[]);
      });
    };
    sortChildren(roots);

    return roots;
  }, []);

  // Memoize tree data to prevent unnecessary recalculations
  const treeData = useMemo(() => (data ? buildTree(data) : []), [data, buildTree]);

  // Helper to find item by ID in tree
  const findItemById = useCallback((itemList: T[], id: string): T | undefined => {
    for (const item of itemList) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children as T[], id);
        if (found) return found;
      }
    }
    return undefined;
  }, []);

  // Filter tree based on search
  const filterTree = useCallback((nodes: T[], search: string): T[] => {
    if (!search) return nodes;

    return nodes.reduce((acc: T[], node) => {
      const searchLower = search.toLowerCase();
      const nameMatches = node.name.toLowerCase().includes(searchLower);
      const codeMatches = node.code?.toLowerCase().includes(searchLower) || false;
      const nodeMatches = nameMatches || codeMatches;
      const filteredChildren = node.children ? filterTree(node.children as T[], search) : [];

      if (nodeMatches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren,
        } as T);
      }

      return acc;
    }, []);
  }, []);

  return {
    treeData,
    findItemById,
    filterTree,
  };
}
